import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  InputBase,
  Chip,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../../constants/color";
import { Icon } from "../../../icons";
import CustomTooltip from "../../../components/CustomTooltip";
import { useAuth } from "../../../context/authContext";
import axios from "axios";
import { url } from "../../../constants/url";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useNavigate } from "react-router-dom";
import SelfVisitReportViewModal from "./SelfVisitReportViewModal";
import toast from "react-hot-toast";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";

// Status styles mapping for Site Visit reports
const statusStyles = {
  SIGNPENDING: { bgcolor: "#A5E3C2", color: "#fff" },
  FINALREPORT: { bgcolor: "#8CD47E", color: "#fff" },
  DRAFT: { bgcolor: "#F8D66D", color: "#fff" },
};

const SelfVisitReports = () => {
  const { userData, isAuthenticated, hasRole, token } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [pageSize] = useState(12);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);

  // Filter and sort states
  const [selectedSort, setSelectedSort] = useState("latest");
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: null,
  });

  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);
  const [filterMenusOpen, setFilterMenusOpen] = useState({
    engineers: false,
    projects: false,
    statuses: false,
  });

  const [downloadingReportId, setDownloadingReportId] = useState(null);
  const [deletingReportId, setDeletingReportId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);

  // Date filter states
  const [dateFilter, setDateFilter] = useState("ALL");
  const [customDateRange, setCustomDateRange] = useState({
    from: null,
    to: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Filter states
  const [engineers, setEngineers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedEngineers, setSelectedEngineers] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    engineers: false,
    projects: false,
    statuses: false,
  });

  // Show filter section state
  const [showFilters, setShowFilters] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Store filter params for debugging
  const filterParamsRef = useRef({
    lastApplied: null,
    lastPage: null,
  });

  const navigate = useNavigate();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Helper function to transform report data
  const transformReports = (reportsData) => {
    return reportsData.map(item => {
      let visitDateDisplay = "N/A";
      if (item.dateOfVisit) {
        try {
          const date = new Date(item.dateOfVisit);
          if (!isNaN(date.getTime())) {
            visitDateDisplay = formatDate(date);
          }
        } catch (error) {
          console.error("Error formatting visit date:", error);
        }
      }

      let engineerName = "N/A";
      let engineerEmail = "";

      if (item.createdBy) {
        if (typeof item.createdBy === 'object') {
          engineerName = item.createdBy.name || "N/A";
          engineerEmail = item.createdBy.email || "";
        } else if (item.userDetails) {
          engineerName = item.userDetails.name || "N/A";
          engineerEmail = item.userDetails.email || "";
        }
      }

      const developersList = Array.isArray(item.developers)
        ? item.developers.join(", ")
        : item.developers || "N/A";

      const location = item.location || "N/A";

      let displayStatus = item.status || "DRAFT";
      let statusText = displayStatus;

      if (displayStatus === "SIGNPENDING") {
        statusText = "Sign Pending";
      } else if (displayStatus === "FINALREPORT") {
        statusText = "Final Report";
      } else if (displayStatus === "DRAFT") {
        statusText = "Draft";
      }

      return {
        id: item._id || item.id,
        projectName: item.projectName || "N/A",
        developers: developersList,
        location: location,
        dateOfVisit: visitDateDisplay,
        engineerName: engineerName,
        engineerEmail: engineerEmail,
        purposeOfVisit: item.purposeOfVisit || "N/A",
        reportDate: item.reportDate || item.createdAt,
        status: displayStatus,
        statusText: statusText,
        _id: item._id,
        rawDateOfVisit: item.dateOfVisit,
        rawProjectName: item.projectName,
        rawEngineerName: engineerName,
      };
    });
  };

  // Helper function to format date
  const formatDate = (date) => {
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    }).replace(/\//g, '/');
  };

  // Enhanced fetchSelfVisitReports with debugging
  const fetchSelfVisitReports = useCallback(async () => {
    if (!isAuthenticated || !token || !(hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // DEBUG: Log filter states
      console.log("🔍 FETCHING DATA - Page:", page);
      console.log("📊 Filter States:", {
        selectedStatuses,
        selectedEngineers,
        selectedProjects,
        searchDebounce,
        dateFilter,
        customDateRange,
        sort: sortConfig.column ? `${sortConfig.column}-${sortConfig.direction}` : selectedSort
      });

      // Determine sort parameter
      let sortParam = "";

      if (sortConfig.column && sortConfig.direction) {
        if (sortConfig.column === 'projectName') {
          sortParam = sortConfig.direction === 'asc' ? 'projectAz' : 'projectZa';
        } else if (sortConfig.column === 'engineerName') {
          sortParam = sortConfig.direction === 'asc' ? 'engineerAz' : 'engineerZa';
        } else if (sortConfig.column === 'dateOfVisit') {
          sortParam = sortConfig.direction === 'asc' ? 'dateAsc' : 'dateDesc';
        }
      } else if (selectedSort) {
        switch (selectedSort) {
          case "latest":
            sortParam = "dateDesc";
            break;
          case "oldest":
            sortParam = "dateAsc";
            break;
          case "AZ":
            sortParam = "projectAz";
            break;
          case "ZA":
            sortParam = "projectZa";
            break;
          default:
            sortParam = "dateDesc";
        }
      } else {
        sortParam = "dateDesc";
      }

      // Prepare date filter for visit date
      let startDate = null;
      let endDate = null;

      if (dateFilter === "TODAY") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        startDate = today.toISOString();

        const todayEnd = new Date(today);
        todayEnd.setHours(23, 59, 59, 999);
        endDate = todayEnd.toISOString();
      } else if (dateFilter === "YESTERDAY") {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);
        startDate = yesterday.toISOString();

        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);
        endDate = yesterdayEnd.toISOString();
      } else if (dateFilter === "TOMORROW") {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);
        startDate = tomorrow.toISOString();

        const tomorrowEnd = new Date(tomorrow);
        tomorrowEnd.setHours(23, 59, 59, 999);
        endDate = tomorrowEnd.toISOString();
      } else if (dateFilter === "CUSTOM" && customDateRange.from && customDateRange.to) {
        const from = new Date(customDateRange.from);
        from.setHours(0, 0, 0, 0);
        startDate = from.toISOString();

        const to = new Date(customDateRange.to);
        to.setHours(23, 59, 59, 999);
        endDate = to.toISOString();
      }

      // Build query parameters
      const params = {
        page: page,
        limit: pageSize,
        sort: sortParam,
      };

      // For ENGINEER role, only show their own reports
      if (hasRole("ENGINEER") && userData?._id) {
        params.userID = userData._id;
      }

      // Add date filters
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      // Add search query if exists
      if (searchDebounce.trim()) {
        params.search = searchDebounce.trim();
      }

      // Add engineer filter (only for ADMIN/SUPERADMIN)
      if ((hasRole("ADMIN") || hasRole("SUPERADMIN")) && selectedEngineers.length > 0) {
        const engineerNames = selectedEngineers;
        params.engineerName = engineerNames.join(",");
      }

      // Add project filter (for all roles)
      if (selectedProjects.length > 0) {
        params.projectName = selectedProjects.join(",");
      }

      // Add status filter - THIS IS CRITICAL
      if (selectedStatuses.length > 0) {
        params.status = selectedStatuses.join(",");
        console.log("✅ Status filter applied:", selectedStatuses);
      }

      // Store filter params for debugging
      filterParamsRef.current = {
        lastApplied: { ...params },
        lastPage: page,
        timestamp: new Date().toISOString()
      };

      console.log("🚀 API Request Params:", params);

      // Fetch reports with all filters from backend
      const response = await axios.get(`${url}/api/self-visit/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });

      console.log("📥 API Response:", {
        total: response.data?.total,
        page: response.data?.page,
        totalPages: response.data?.totalPages,
        dataLength: response.data?.data?.length,
        hasData: !!response.data?.data
      });

      let reportsData = response.data?.data || [];
      const total = response.data?.total || reportsData.length;

      // Transform data for display
      setReports(transformReports(reportsData));
      setTotalReports(total);
      setTotalPages(Math.ceil(total / pageSize));

    } catch (err) {
      console.error("❌ Error fetching self-visit reports:", err);
      console.error("Error details:", err.response?.data);
      setError(err.response?.data?.message || "Failed to load self-visit reports");
      setReports([]);
      setTotalReports(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [
    isAuthenticated, token, hasRole, userData, page, pageSize,
    selectedSort, sortConfig, dateFilter, customDateRange,
    selectedEngineers, selectedProjects, selectedStatuses, searchDebounce
  ]);

  // Main useEffect for fetching data
  useEffect(() => {
    console.log("🔄 useEffect triggered - Fetching data");
    if (isAuthenticated && (hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
      fetchSelfVisitReports();
    } else {
      setLoading(false);
    }
  }, [
    page, // This ensures fetch when page changes
    fetchSelfVisitReports,
    isAuthenticated,
    hasRole
  ]);

  // Effect for filters - reset to page 1 when filters change
  useEffect(() => {
    console.log("🎯 Filter changed - resetting to page 1");
    setPage(1);
  }, [
    selectedSort,
    sortConfig.column,
    sortConfig.direction,
    dateFilter,
    customDateRange.from,
    customDateRange.to,
    selectedEngineers,
    selectedProjects,
    selectedStatuses,
    searchDebounce,
  ]);

  // Fetch filter data (engineers, projects, statuses)
  const fetchFilterData = async () => {
    try {
      // Fetch engineers (only for ADMIN/SUPERADMIN)
      if ((hasRole("ADMIN") || hasRole("SUPERADMIN")) && !engineers.length) {
        setLoadingFilters(prev => ({ ...prev, engineers: true }));
        const engineersRes = await axios.get(`${url}/api/users/fetch`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            role: "ENGINEER",
            status: "APPROVED",
            sort: "az",
            limit: 100,
            fields: "name,email,_id"
          }
        });

        const uniqueEngineers = [];
        const seenNames = new Set();

        engineersRes.data?.users?.forEach(user => {
          if (user.name && !seenNames.has(user.name)) {
            seenNames.add(user.name);
            uniqueEngineers.push({
              name: user.name,
              id: user._id
            });
          }
        });

        setEngineers(uniqueEngineers);
        setLoadingFilters(prev => ({ ...prev, engineers: false }));
      }

      // Fetch unique project names - For all roles
      try {
        const params = {
          limit: 100,
          fields: "projectName",
          sort: "projectAz"
        };

        if (hasRole("ENGINEER") && userData?._id) {
          params.userID = userData._id;
        }

        const projectsRes = await axios.get(`${url}/api/self-visit/fetch`, {
          headers: { Authorization: `Bearer ${token}` },
          params: params
        });

        const uniqueProjects = [];
        const seenProjects = new Set();

        projectsRes.data?.data?.forEach(report => {
          if (report.projectName && !seenProjects.has(report.projectName)) {
            seenProjects.add(report.projectName);
            uniqueProjects.push({ name: report.projectName });
          }
        });

        setProjects(uniqueProjects.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (err) {
        console.error("Error fetching projects for filter:", err);
      }

    } catch (err) {
      console.error("Error fetching filter data:", err);
    }
  };

  // Effect to fetch filter options when showFilters changes
  useEffect(() => {
    if (showFilters && isAuthenticated) {
      fetchFilterData();
    }
  }, [showFilters, isAuthenticated]);

  // Auto-refresh when screen comes into focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        console.log("🔄 Screen focused - refreshing data");
        fetchSelfVisitReports();
      }
    };

    const handleFocus = () => {
      if (isAuthenticated) {
        console.log("🔄 Window focused - refreshing data");
        fetchSelfVisitReports();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated, fetchSelfVisitReports]);

  // Handle page change
  const handlePageChange = (newPage) => {
    console.log("📄 Changing page from", page, "to", newPage);
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Calculate displayed range
  const startIndex = totalReports > 0 ? ((page - 1) * pageSize) + 1 : 0;
  const endIndex = Math.min(page * pageSize, totalReports);

  // Check if any filter is applied
  const isFilterApplied = () => {
    return selectedSort !== "latest" ||
      sortConfig.column !== null ||
      dateFilter !== "ALL" ||
      selectedEngineers.length > 0 ||
      selectedProjects.length > 0 ||
      selectedStatuses.length > 0 ||
      searchQuery.trim() !== "";
  };

  // Clear all filters
  const clearFilters = () => {
    console.log("🧹 Clearing all filters");
    setSelectedSort("latest");
    setSortConfig({ column: null, direction: null });
    setDateFilter("ALL");
    setCustomDateRange({ from: null, to: null });
    setSelectedEngineers([]);
    setSelectedProjects([]);
    setSelectedStatuses([]);
    setSearchQuery("");
    setPage(1);
  };

  // Clear search only
  const clearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  // Toggle filter section
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  // Handle column sort click
  const handleColumnSort = (column) => {
    const newSortConfig = {
      column: column,
      direction: sortConfig.column === column && sortConfig.direction === 'asc' ? 'desc' : 'asc'
    };

    setSortConfig(newSortConfig);
    setSelectedSort("");
    setPage(1);
  };

  // Handle global sort selection
  const handleGlobalSort = (sortValue) => {
    setSelectedSort(sortValue);
    setSortConfig({ column: null, direction: null });
    setSortAnchorEl(null);
    setPage(1);
  };

  // Handle date filter selection
  const handleDateFilter = (filterValue) => {
    if (filterValue === "CUSTOM") {
      setShowDatePicker(true);
      setCalendarAnchorEl(null);
    } else {
      setDateFilter(filterValue);
      setCalendarAnchorEl(null);
      setPage(1);
    }
  };

  // Handle engineer filter change (only for ADMIN/SUPERADMIN)
  const handleEngineerFilterChange = (event) => {
    const value = event.target.value;
    setSelectedEngineers(value);
    setPage(1);
  };

  // Handle project filter change (for all roles)
  const handleProjectFilterChange = (event) => {
    const value = event.target.value;
    setSelectedProjects(value);
    setPage(1);
  };

  // Handle status filter change - FIXED
  const handleStatusFilterChange = (event) => {
    const value = event.target.value;
    console.log("🎯 Status filter changed to:", value);
    setSelectedStatuses(value);
    setPage(1);
  };

  // Get sort icon and color for a column
  const getSortIconForColumn = (column) => {
    if (sortConfig.column === column) {
      return sortConfig.direction === 'asc' ? 'foldUp' : 'foldDown';
    }
    return 'sort';
  };

  const getSortIconColorForColumn = (column) => {
    if (sortConfig.column === column) {
      return COLORS.PRIMARY;
    }
    return COLORS.LIGHT_SECONDARY;
  };

  // Handle view report
  const handleViewReport = (reportId) => {
    setSelectedReportId(reportId);
    setViewModalOpen(true);
  };

  // Handle edit report
  const handleEditReport = (reportId) => {
    navigate(`/self-visit-reports/edit/${reportId}`);
  };

  // Handle download report
  const handleDownloadReport = async (reportId) => {
    try {
      if (!token) {
        alert("Please login to download reports");
        return;
      }

      setDownloadingReportId(reportId);

      const response = await axios.get(
        `${url}/api/self-visit/download-report/${reportId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        }
      );

      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const report = reports.find(
        (r) => r.id === reportId || r._id === reportId,
      );
      if (report) {
        const date = new Date(
          report.rawDateOfVisit || report.reportDate || new Date(),
        );
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        const cleanProjectName = (report.projectName || "SiteVisit")
          .replace(/[^a-zA-Z0-9]/g, "_")
          .replace(/_+/g, "_")
          .replace(/^_|_$/g, "")
          .substring(0, 50);
        link.download = `SVR_${cleanProjectName}_${year}_${month}_${day}.pdf`;
      } else {
        link.download = `self-visit-report-${reportId}.pdf`;
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
      console.error("Error downloading report:", err);
      alert(err.response?.data?.message || "Failed to download report");
    } finally {
      setDownloadingReportId(null);
    }
  };

  // Handle delete report
  const handleDeleteReport = async (reportId) => {
    setReportToDelete(reportId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;

    try {
      setDeletingReportId(reportToDelete);

      await axios.delete(`${url}/api/self-visit/delete/${reportToDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Self-visit report deleted successfully");
      // Refresh data after deletion
      fetchSelfVisitReports();

    } catch (err) {
      console.error("Error deleting report:", err);
      toast.error(err.response?.data?.message || "Failed to delete report");
    } finally {
      setDeletingReportId(null);
      setDeleteConfirmOpen(false);
      setReportToDelete(null);
    }
  };

  // Handle add new report
  const handleAddNewReport = () => {
    navigate("/self-visit-reports/add");
  };

  // Get available statuses for filter
  const getAvailableStatuses = () => {
    return [
      { label: "Sign Pending", value: "SIGNPENDING" },
      { label: "Final Report", value: "FINALREPORT" },
      { label: "Draft", value: "DRAFT" },
    ];
  };

  // Define table headers with role-based columns
  const getTableHeaders = () => {
    const headers = [
      { key: "Project Name", hasSort: true, sortKey: "projectName", width: "200px" },
    ];

    headers[0].subKey = "developers";

    if (hasRole("ADMIN") || hasRole("SUPERADMIN")) {
      headers.push({ key: "Engineer", hasSort: true, sortKey: "engineerName", width: "140px" });
    }

    headers.push(
      { key: "Date of Visit", hasSort: true, sortKey: "dateOfVisit", width: "100px" },
      { key: "Purpose", hasSort: false, width: "170px" },
      { key: "Status", hasSort: false, width: "90px" },
      { key: "Actions", hasSort: false, width: "90px" }
    );

    return headers;
  };

  // Show message if user doesn't have access
  if (!isAuthenticated || !(hasRole("ADMIN") || hasRole("SUPERADMIN") || hasRole("ENGINEER"))) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          This page is only accessible to ADMIN, SUPERADMIN, or ENGINEER users.
        </Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              sx={{ ...TYPOGRAPHY.H2, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}
            >
              Site Visit Reports
            </Typography>

            <Button
              startIcon={<Icon name="plus2" />}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                ...TYPOGRAPHY.H4,
                px: 2.5,
                "&:hover": { bgcolor: COLORS.SECONDARY },
              }}
              onClick={handleAddNewReport}
            >
              Add Site Visit Report
            </Button>
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
            {/* Search Bar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexGrow: 0,
                width: { xs: 180, sm: 250, md: 280 },
                px: 2,
                py: 0.5,
                borderRadius: 1.5,
                border: `1px solid ${COLORS.PRIMARY}`,
                backgroundColor: COLORS.BACKGROUND,
                color: COLORS.PRIMARY,
              }}
            >
              <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
              <InputBase
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    fetchSelfVisitReports();
                  }
                }}
                sx={{
                  flex: 1,
                  fontFamily: FONT_FAMILY,
                  ...TYPOGRAPHY.BODY_STANDARD,
                  color: COLORS.TEXT,
                  ml: 1,
                  mt: 0.5,
                  '& input::placeholder': {
                    fontSize: '15px',
                  },
                }}
              />
              {searchQuery && (
                <IconButton
                  size="small"
                  onClick={clearSearch}
                  sx={{ padding: 0.5 }}
                >
                  <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
                </IconButton>
              )}
            </Box>

            {/* Calendar Menu */}
            <CustomTooltip title="Calendar Filter">
              <IconButton
                onClick={(e) => setCalendarAnchorEl(e.currentTarget)}
                sx={{
                  color: dateFilter !== "ALL" ? COLORS.PRIMARY : COLORS.SECONDARY,
                  padding: '6px'
                }}
              >
                <Icon name="calendar2" size={20} />
              </IconButton>
            </CustomTooltip>

            {/* Sort Menu */}
            <CustomTooltip title="Sort Options">
              <IconButton
                onClick={(e) => setSortAnchorEl(e.currentTarget)}
                sx={{
                  color: selectedSort !== "latest" || sortConfig.column ? COLORS.PRIMARY : COLORS.SECONDARY,
                  padding: '6px'
                }}
              >
                <Icon name="sort" size={20} />
              </IconButton>
            </CustomTooltip>

            {/* Filter Button */}
            <CustomTooltip title="Show Filters">
              <IconButton
                onClick={toggleFilters}
                sx={{
                  color: showFilters ? COLORS.PRIMARY : COLORS.SECONDARY,
                  padding: '6px'
                }}
              >
                <Icon name="list" size={20} />
              </IconButton>
            </CustomTooltip>

            {isFilterApplied() && (
              <CustomTooltip title="Clear Filters">
                <IconButton onClick={clearFilters} sx={{ padding: '6px' }}>
                  <Icon name="rotate" color={COLORS.ERROR} size={20} />
                </IconButton>
              </CustomTooltip>
            )}
          </Box>
        </Box>

        {/* Filter Section */}
        {showFilters && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            alignItems: 'flex-start',
            flexWrap: 'wrap'
          }}>
            {/* Engineer Filter (only for ADMIN/SUPERADMIN) */}
            {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography
                  sx={{
                    ...TYPOGRAPHY.BODY_SMALL,
                    color: COLORS.PRIMARY,
                    mb: 0.5,
                    fontWeight: 600
                  }}
                >
                  Engineer
                </Typography>
                <FormControl fullWidth size="small">
                  <Select
                    multiple
                    value={selectedEngineers}
                    onChange={handleEngineerFilterChange}
                    open={filterMenusOpen.engineers}
                    onOpen={() => setFilterMenusOpen(prev => ({ ...prev, engineers: true }))}
                    onClose={() => setFilterMenusOpen(prev => ({ ...prev, engineers: false }))}
                    input={<OutlinedInput />}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return (
                          <Typography sx={{
                            color: COLORS.LIGHT_SECONDARY,
                            fontSize: '0.875rem'
                          }}>
                            All Engineers
                          </Typography>
                        );
                      }
                      return selected.length <= 2
                        ? selected.join(', ')
                        : `${selected.length} selected`;
                    }}
                    displayEmpty
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLORS.PRIMARY,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                          width: 280,
                          overflow: 'auto',
                          mt: 0.5,
                          '& .MuiMenuItem-root': {
                            minHeight: 40,
                            padding: '6px 12px',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '6px',
                          }
                        }
                      },
                    }}
                  >
                    {loadingFilters.engineers ? (
                      <MenuItem disabled sx={{ justifyContent: 'center' }}>
                        <CircularProgress size={20} />
                      </MenuItem>
                    ) : engineers.length > 0 ? (
                      engineers.map((engineer, index) => (
                        <MenuItem key={engineer.id || index} value={engineer.name}>
                          <Checkbox checked={selectedEngineers.indexOf(engineer.name) > -1} size="small" />
                          <ListItemText
                            primary={engineer.name}
                            sx={{
                              '& .MuiTypography-root': {
                                fontSize: '0.85rem',
                              }
                            }}
                          />
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No engineers available</MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Project Filter - Available for all roles */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Project
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedProjects}
                  onChange={handleProjectFilterChange}
                  open={filterMenusOpen.projects}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, projects: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, projects: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          All Projects
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {projects.map((project, index) => (
                    <MenuItem key={index} value={project.name}>
                      <Checkbox checked={selectedProjects.indexOf(project.name) > -1} size="small" />
                      <ListItemText
                        primary={project.name}
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Status Filter - Available for all roles */}
            <Box sx={{ flex: 1, minWidth: 180 }}>
              <Typography
                sx={{
                  ...TYPOGRAPHY.BODY_SMALL,
                  color: COLORS.PRIMARY,
                  mb: 0.5,
                  fontWeight: 600
                }}
              >
                Status
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  multiple
                  value={selectedStatuses}
                  onChange={handleStatusFilterChange}
                  open={filterMenusOpen.statuses}
                  onOpen={() => setFilterMenusOpen(prev => ({ ...prev, statuses: true }))}
                  onClose={() => setFilterMenusOpen(prev => ({ ...prev, statuses: false }))}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <Typography sx={{
                          color: COLORS.LIGHT_SECONDARY,
                          fontSize: '0.875rem'
                        }}>
                          All Statuses
                        </Typography>
                      );
                    }
                    return selected.length <= 2
                      ? selected.map(s => s === "SIGNPENDING" ? "Sign Pending" :
                        s === "FINALREPORT" ? "Final Report" : "Draft").join(', ')
                      : `${selected.length} selected`;
                  }}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: COLORS.PRIMARY,
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 250,
                        width: 280,
                        overflow: 'auto',
                        mt: 0.5,
                        '& .MuiMenuItem-root': {
                          minHeight: 40,
                          padding: '6px 12px',
                        },
                        '& .MuiCheckbox-root': {
                          padding: '6px',
                        }
                      }
                    },
                  }}
                >
                  {getAvailableStatuses().map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      <Checkbox checked={selectedStatuses.indexOf(status.value) > -1} size="small" />
                      <ListItemText
                        primary={status.label}
                        sx={{
                          '& .MuiTypography-root': {
                            fontSize: '0.85rem',
                          }
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}



        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Table Container */}
        <Box sx={{
          width: '100%',
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: '#f1f1f1',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: COLORS.LIGHT_SECONDARY,
            borderRadius: '4px',
          },
        }}>
          <Paper elevation={0} sx={{
            backgroundColor: "transparent",
            minWidth: hasRole("ADMIN") || hasRole("SUPERADMIN") ? '950px' : '810px'
          }}>
            <TableContainer>
              <Table
                sx={{
                  backgroundColor: "transparent",
                  "& thead th": {
                    backgroundColor: "transparent",
                    padding: "8px 4px",
                  },
                  "& tbody td": {
                    backgroundColor: "transparent",
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    padding: "8px 4px",
                    verticalAlign: 'top',
                  },
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <TableHead>
                  <TableRow>
                    {getTableHeaders().map((head) => (
                      <TableCell
                        key={head.key}
                        sx={{
                          ...TYPOGRAPHY.H4,
                          color: COLORS.PRIMARY,
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          whiteSpace: "nowrap",
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          width: head.width,
                          minWidth: head.width,
                          maxWidth: head.width,
                          padding: "8px 4px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 0.5,
                          }}
                        >
                          <span style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {head.key}
                          </span>
                          {head.hasSort && (
                            <CustomTooltip title={`Sort by ${head.key}`}>
                              <Icon
                                name={getSortIconForColumn(head.sortKey)}
                                color={getSortIconColorForColumn(head.sortKey)}
                                sx={{
                                  fontSize: 14,
                                  cursor: "pointer",
                                  flexShrink: 0,
                                  "&:hover": {
                                    color: COLORS.PRIMARY,
                                  },
                                }}
                                onClick={() => handleColumnSort(head.sortKey)}
                              />
                            </CustomTooltip>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={getTableHeaders().length} align="center" sx={{ py: 3 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : reports.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={getTableHeaders().length} align="center" sx={{ py: 3 }}>
                        <Typography sx={{ color: COLORS.TEXT }}>
                          {!isFilterApplied()
                            ? (hasRole("ENGINEER") ? "You haven't created any self-visit reports yet." :
                              "No self-visit reports found.")
                            : `No reports found with selected filters`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    reports.map((report) => (
                      <TableRow key={report.id} hover sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                        {/* Project Name Column */}
                        <TableCell sx={{
                          width: '200px',
                          minWidth: '200px',
                          maxWidth: '200px',
                          padding: "8px 4px"
                        }}>
                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_LARGE,
                              color: COLORS.TEXT,
                              fontSize: "0.8rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.3,
                              mb: 0.5,
                              fontWeight: 600
                            }}
                          >
                            {report.projectName}
                          </Typography>

                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.SECONDARY,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2,
                              mb: 0.5
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 600, color: COLORS.TEXT }}>
                              Developers:
                            </Box> {report.developers}
                          </Typography>

                          <Typography
                            sx={{
                              ...TYPOGRAPHY.BODY_SMALL,
                              color: COLORS.SECONDARY,
                              fontSize: "0.7rem",
                              wordBreak: 'break-word',
                              lineHeight: 1.2
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 600, color: COLORS.TEXT }}>
                              Location:
                            </Box> {report.location}
                          </Typography>
                        </TableCell>

                        {/* Engineer Column */}
                        {(hasRole("ADMIN") || hasRole("SUPERADMIN")) && (
                          <TableCell sx={{
                            width: '140px',
                            minWidth: '140px',
                            maxWidth: '140px',
                            padding: "8px 4px"
                          }}>
                            <Typography
                              sx={{
                                ...TYPOGRAPHY.BODY_LARGE,
                                color: COLORS.TEXT,
                                fontSize: "0.8rem",
                                wordBreak: 'break-word',
                                lineHeight: 1.3,
                                mb: 0.25
                              }}
                            >
                              {report.engineerName}
                            </Typography>
                            {report.engineerEmail && (
                              <Typography
                                sx={{
                                  ...TYPOGRAPHY.BODY_SMALL,
                                  color: COLORS.SECONDARY,
                                  fontSize: "0.7rem",
                                  wordBreak: 'break-word',
                                  lineHeight: 1.2
                                }}
                              >
                                {report.engineerEmail}
                              </Typography>
                            )}
                          </TableCell>
                        )}

                        {/* Date of Visit Column */}
                        <TableCell
                          sx={{
                            width: '100px',
                            minWidth: '100px',
                            maxWidth: '100px',
                            padding: "8px 4px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {report.dateOfVisit}
                        </TableCell>

                        {/* Purpose Column */}
                        <TableCell
                          sx={{
                            width: '170px',
                            minWidth: '170px',
                            maxWidth: '170px',
                            padding: "8px 4px",
                            color: COLORS.TEXT,
                            fontSize: "0.8rem",
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                          }}
                        >
                          {report.purposeOfVisit}
                        </TableCell>

                        {/* Status Column */}
                        <TableCell sx={{
                          width: '90px',
                          minWidth: '90px',
                          maxWidth: '90px',
                          padding: "8px 4px"
                        }}>
                          <Chip
                            label={report.statusText}
                            size="small"
                            sx={{
                              ...(statusStyles[report.status] || statusStyles.DRAFT),
                              height: 20,
                              ...TYPOGRAPHY.BODY_SMALL,
                              fontSize: "0.65rem",
                              textTransform: 'capitalize',
                              '& .MuiChip-label': {
                                padding: '0 4px',
                              }
                            }}
                          />
                        </TableCell>

                        {/* Actions Column */}
                        <TableCell sx={{
                          width: '90px',
                          minWidth: '90px',
                          maxWidth: '90px',
                          padding: "8px 4px"
                        }}>
                          <Box sx={{ display: "flex", gap: 0.25, flexWrap: 'nowrap' }}>
                            {/* View Report */}
                            <CustomTooltip title="View Report">
                              <IconButton
                                size="small"
                                onClick={() => handleViewReport(report._id)}
                                sx={{
                                  padding: "2px",
                                  minWidth: '20px',
                                  height: '20px',
                                }}
                              >
                                <Icon name="Action1" color={COLORS.TEXT} size={16} />
                              </IconButton>
                            </CustomTooltip>

                            {/* Edit Report */}
                            <CustomTooltip title="Edit Report">
                              <IconButton
                                size="small"
                                onClick={() => handleEditReport(report._id)}
                                sx={{
                                  padding: "2px",
                                  minWidth: '20px',
                                  height: '20px',
                                }}
                              >
                                <Icon name="Action3" color="#8CD47E" size={16} />
                              </IconButton>
                            </CustomTooltip>

                            {/* Download Report - Only for FINALREPORT status */}
                            {report.status === "FINALREPORT" && (
                              <CustomTooltip title="Download Report">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadReport(report._id)}
                                  disabled={downloadingReportId === report._id}
                                  sx={{
                                    padding: "2px",
                                    minWidth: '20px',
                                    height: '20px',
                                  }}
                                >
                                  {downloadingReportId === report._id ? (
                                    <CircularProgress size={14} color="inherit" />
                                  ) : (
                                    <Icon name="download1" color={COLORS.SECONDARY} size={16} />
                                  )}
                                </IconButton>
                              </CustomTooltip>
                            )}

                            {/* Delete Report */}
                            <CustomTooltip title="Delete Report">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteReport(report._id)}
                                disabled={deletingReportId === report._id}
                                sx={{
                                  padding: "2px",
                                  minWidth: '20px',
                                  height: '20px',
                                }}
                              >
                                {deletingReportId === report._id ? (
                                  <CircularProgress size={14} color="inherit" />
                                ) : (
                                  <Icon name="Action4" color={COLORS.ERROR} size={16} />
                                )}
                              </IconButton>
                            </CustomTooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && reports.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                  mt: 2,
                  width: '100%',
                  overflowX: 'auto',
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#777", whiteSpace: 'nowrap' }}>
                  {startIndex} - {endIndex} of {totalReports} reports
                </Typography>

                <Box sx={{ display: "flex", gap: 1, alignItems: 'center' }}>
                  <IconButton
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-left" size={18} />
                  </IconButton>

                  {Array.from({ length: Math.min(totalPages, 3) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 3) {
                      pageNum = i + 1;
                    } else if (page === 1) {
                      pageNum = i + 1;
                    } else if (page === totalPages) {
                      pageNum = totalPages - 2 + i;
                    } else {
                      pageNum = page - 1 + i;
                    }

                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <Button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          sx={{
                            minWidth: 24,
                            height: 24,
                            fontSize: "0.7rem",
                            bgcolor: page === pageNum ? COLORS.PRIMARY : "transparent",
                            color: page === pageNum ? "#fff" : COLORS.TEXT,
                            border: page === pageNum ? "none" : `1px solid ${COLORS.LIGHT_SECONDARY}`,
                            "&:hover": {
                              bgcolor: page === pageNum ? COLORS.PRIMARY : "rgba(0,0,0,0.04)"
                            },
                          }}
                        >
                          {pageNum}
                        </Button>
                      );
                    }
                    return null;
                  })}

                  <IconButton
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    size="small"
                    sx={{ padding: '4px' }}
                  >
                    <Icon name="chevron-right" size={18} />
                  </IconButton>
                </Box>
              </Box>
            )}
          </Paper>
        </Box>

        {/* Global Sort Menu */}
        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={() => setSortAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "Latest First", value: "latest", icon: "foldUp" },
            { label: "Oldest First", value: "oldest", icon: "foldDown" },
            { label: "Project A-Z", value: "AZ", icon: "foldUp" },
            { label: "Project Z-A", value: "ZA", icon: "foldDown" },
          ].map((item) => {
            const isActive = selectedSort === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => handleGlobalSort(item.value)}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  bgcolor: "transparent !important",
                  "&:hover": { bgcolor: "transparent" },
                  "&.Mui-selected": { bgcolor: "transparent" },
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        {/* Calendar Menu */}
        <Menu
          anchorEl={calendarAnchorEl}
          open={Boolean(calendarAnchorEl)}
          onClose={() => setCalendarAnchorEl(null)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: "0px 6px 18px rgba(0,0,0,0.15)",
              px: 0.5,
            },
          }}
        >
          {[
            { label: "All Reports", value: "ALL", icon: "calendarAll" },
            { label: "Today", value: "TODAY", icon: "calendarToday" },
            { label: "Yesterday", value: "YESTERDAY", icon: "calendarYesterday" },
            { label: "Tomorrow", value: "TOMORROW", icon: "calendarTomorrow" },
            { label: "Custom Date Range", value: "CUSTOM", icon: "calendarCustom" },
          ].map((item) => {
            const isActive = dateFilter === item.value;

            return (
              <MenuItem
                key={item.value}
                onClick={() => handleDateFilter(item.value)}
                sx={{
                  gap: 1.5,
                  borderRadius: 1,
                  bgcolor: "transparent !important",
                  "&:hover": { bgcolor: "transparent" },
                  "&.Mui-selected": { bgcolor: "transparent" },
                  color: isActive ? COLORS.PRIMARY : COLORS.TEXT,
                }}
              >
                <Icon
                  name={item.icon}
                  size={18}
                  color={isActive ? COLORS.PRIMARY : COLORS.TEXT}
                />
                <Typography sx={TYPOGRAPHY.BODY_LARGE}>
                  {item.label}
                </Typography>
              </MenuItem>
            );
          })}
        </Menu>

        <SelfVisitReportViewModal
          open={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setSelectedReportId(null);
          }}
          reportId={selectedReportId}
        />

        {/* Custom Date Range Picker Dialog */}
        <Dialog
          open={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="calendarCustom" color={COLORS.PRIMARY} />
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>
                Select Date Range
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={customDateRange.from}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, from: newValue }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                maxDate={customDateRange.to || new Date()}
              />
              <DatePicker
                label="To Date"
                value={customDateRange.to}
                onChange={(newValue) => {
                  setCustomDateRange(prev => ({ ...prev, to: newValue }));
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                minDate={customDateRange.from}
                maxDate={new Date()}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setShowDatePicker(false)}
              sx={{
                color: COLORS.TEXT,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (customDateRange.from && customDateRange.to) {
                  setDateFilter("CUSTOM");
                  setShowDatePicker(false);
                  setPage(1);
                }
              }}
              disabled={!customDateRange.from || !customDateRange.to}
              sx={{
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                "&:hover": { bgcolor: COLORS.SECONDARY },
                "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY }
              }}
            >
              Apply Date Range
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={deleteConfirmOpen}
          title="Delete Self Visit Report"
          content="Are you sure you want to delete this self-visit report? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setDeleteConfirmOpen(false);
            setReportToDelete(null);
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default SelfVisitReports;